"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import type { Lot } from "@/app/page";

const FILTERS = [
  { label: "All Inventory", value: "all" },
  { label: "Live Now", value: "live" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Sneakers", value: "Sneakers" },
  { label: "Apparel", value: "Apparel" },
  { label: "Accessories", value: "Accessories" },
  { label: "Electronics", value: "Electronics" },
  { label: "Collectibles", value: "Collectibles" },
  { label: "Other", value: "other" },
];

const SORTS = [
  { label: "Recently Added", value: "recent" },
  { label: "Ending Soon", value: "ending" },
  { label: "Highest Margin", value: "margin" },
  { label: "Lowest Unit Cost", value: "lowcost" },
  { label: "Most Units", value: "units" },
];

const CATEGORIES = [
  "Sneakers",
  "Apparel",
  "Accessories",
  "Electronics",
  "Collectibles",
];

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

function Ico({ d, className }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
const PIN = "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z M12 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z";
const TRUCK = "M14 18V6H2v12h1a2 2 0 1 0 4 0h4M14 8h4l4 5v5h-2a2 2 0 1 1-4 0h-1";
const TAG = "M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4h8a2 2 0 0 1 1.4.6l8.4 8.4a2 2 0 0 1 0 2.8Z M7 7h.01";
const BOX = "M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z M3.3 7 12 12l8.7-5 M12 22V12";
const COIN = "M12 2v20 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6";
const FILE = "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z M14 2v6h6 M9 13h6 M9 17h6";
const CALC = "M4 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z M8 6h8 M8 10h.01 M12 10h.01 M16 10h.01 M8 14h.01 M12 14h.01 M8 18h4";

// One logistics/economics chip (icon + label) in the card's detail row.
function Chip({ d, children, tone = "" }: { d: string; children: React.ReactNode; tone?: string }) {
  return (
    <span className={`flex items-center gap-1 ${tone || "text-gray-400"}`}>
      <Ico d={d} className="w-3 h-3 shrink-0" />
      {children}
    </span>
  );
}

// One line in the profit & cost breakdown.
function Row({ label, value, valueClass = "text-white" }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-400">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

// Lot card (C4).
function LotCard({ lot }: { lot: Lot }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const moq = lot.min_order_units || 1;
  const moqPrice = moq * lot.cost_price;
  const takeAllPrice = lot.units_available * lot.cost_price;
  // Economics at the full-lot (Take All) quantity.
  const revenue = lot.units_available * lot.msrp;
  const totalCost = takeAllPrice;
  const profit = revenue - totalCost;
  const roi = totalCost > 0 ? Math.round((profit / totalCost) * 100) : 0;
  const marginPct = revenue > 0 ? Math.round((profit / revenue) * 100) : 0;
  const shipText =
    lot.ship_time || (lot.freight_included ? "Freight included" : "Plus freight");
  const vsMsrp =
    lot.msrp && lot.cost_price
      ? Math.round(((lot.msrp - lot.cost_price) / lot.msrp) * 100)
      : 0;
  const badge =
    lot.status === "live"
      ? { label: "LIVE", color: "#22c55e" }
      : lot.status === "pending"
      ? { label: "UPCOMING", color: "#3b82f6" }
      : { label: "SOLD OUT", color: "#6b7280" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/5 hover:border-white/15 transition-colors"
    >
      <div className="relative h-52 overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={lot.image_url || ""}
          alt={lot.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {lot.status === "live" ? (
            // Live lots pulse to read as "selling right now".
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-[0_0_12px_rgba(34,197,94,0.7)]"
              style={{ backgroundColor: badge.color }}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
              </span>
              {badge.label}
            </span>
          ) : (
            <span
              className="px-2.5 py-1 rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: badge.color }}
            >
              {badge.label}
            </span>
          )}
          {lot.is_hot && (
            <span className="flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
              <svg
                className="w-2.5 h-2.5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 2c1 3 4 4 4 8a4 4 0 0 1-8 0c0-1 .5-2 1-3 .5 2 2 2 3 3 .5-2-1-4 0-6-1 0-3 1-4 3-1-3 3-5 4-8Z" />
              </svg>
              HOT
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
          {lot.category} &middot; {lot.units_available.toLocaleString()} units
        </p>
        <h3 className="font-semibold text-white text-sm mb-3 leading-tight">
          {lot.title}
        </h3>

        {/* Location / Shipping / % off MSRP / MOQ / Cost, in one tidy row. */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] mb-3.5">
          {lot.location && <Chip d={PIN}>{lot.location}</Chip>}
          <Chip d={TRUCK}>{shipText}</Chip>
          {vsMsrp > 0 && (
            <Chip d={TAG} tone="text-green-400">
              {vsMsrp}% off MSRP
            </Chip>
          )}
          <Chip d={BOX}>MOQ {moq.toLocaleString()}</Chip>
          <Chip d={COIN}>{usd(lot.cost_price)}/unit</Chip>
        </div>

        {lot.status === "live" ? (
          <>
            {/* MOQ Price + Take All Price */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-xl bg-white/[0.03] border border-white/8 px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest text-gray-500 mb-0.5">
                  MOQ &middot; {moq.toLocaleString()}
                </p>
                <p className="text-sm font-bold text-white tabular-nums">
                  {usd(moqPrice)}
                </p>
              </div>
              <div className="rounded-xl bg-blue-600/10 border border-blue-500/25 px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest text-blue-400/80 mb-0.5">
                  Take All &middot; {lot.units_available.toLocaleString()}
                </p>
                <p className="text-sm font-bold text-blue-300 tabular-nums">
                  {usd(takeAllPrice)}
                </p>
              </div>
            </div>

            {/* Full Manifest + Profit & Cost Breakdown */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`/listing/${lot.id}/manifest`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white border border-white/10 rounded-lg py-2.5 hover:bg-white/5 transition-colors"
              >
                <Ico d={FILE} className="w-3.5 h-3.5" /> Full Manifest
              </a>
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                aria-expanded={showBreakdown}
                className={`flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg py-2.5 transition-colors ${
                  showBreakdown
                    ? "bg-white/10 text-white border border-white/15"
                    : "text-white border border-white/10 hover:bg-white/5"
                }`}
              >
                <Ico d={CALC} className="w-3.5 h-3.5" /> Profit &amp; Cost
              </button>
            </div>

            {/* Generated profit & cost breakdown (full-lot economics). */}
            {showBreakdown && (
              <div className="mt-3 rounded-xl bg-gradient-to-br from-blue-600/5 to-transparent border border-blue-500/15 p-3.5 space-y-1.5">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Full-lot breakdown &middot; {lot.units_available.toLocaleString()} units
                </p>
                <Row label="Cost (Clearline)" value={usd(totalCost)} valueClass="text-blue-300" />
                <Row label="Revenue (MSRP)" value={usd(revenue)} />
                <div className="flex justify-between items-center pt-1.5 border-t border-white/5">
                  <span className="text-xs text-gray-400 font-medium">Est. profit</span>
                  <span className="text-base font-bold text-green-400 tabular-nums">
                    +{usd(profit)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="rounded-lg bg-black/20 px-3 py-1.5">
                    <p className="text-[9px] uppercase tracking-widest text-gray-500 mb-0.5">ROI</p>
                    <p className="text-sm font-bold text-green-400 tabular-nums">{roi}%</p>
                  </div>
                  <div className="rounded-lg bg-black/20 px-3 py-1.5">
                    <p className="text-[9px] uppercase tracking-widest text-gray-500 mb-0.5">Margin</p>
                    <p className="text-sm font-bold text-white tabular-nums">{marginPct}%</p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <span
            aria-disabled="true"
            className="mt-1 block text-center text-xs font-semibold text-gray-500 border border-white/5 rounded-lg py-2.5 cursor-not-allowed select-none"
          >
            {lot.status === "sold" ? "Sold Out" : "Coming Soon"}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function LotGrid({
  live = [],
  upcoming = [],
  personalized = false,
  authenticated = false,
}: {
  live?: Lot[];
  upcoming?: Lot[];
  // The server has already ordered `live` so the buyer's onboarding categories
  // come first. When true, offer a "Recommended" sort (the default) that keeps
  // that server order instead of re-sorting it away.
  personalized?: boolean;
  // Signed-in buyers see the section framed as personalized picks.
  authenticated?: boolean;
}) {
  const [filter, setFilter] = useState("all");
  // Default the sort to "Ending Soon" so the closing lots always lead.
  const [sort, setSort] = useState("ending");
  const combined = useMemo(() => [...live, ...upcoming], [live, upcoming]);
  const sortOptions = personalized
    ? [{ label: "Recommended", value: "recommended" }, ...SORTS]
    : SORTS;

  const visible = useMemo(() => {
    let list = [...combined];
    if (filter === "live") list = list.filter((p) => p.status === "live");
    else if (filter === "upcoming")
      list = list.filter((p) => p.status === "pending");
    else if (CATEGORIES.includes(filter))
      list = list.filter((p) => p.category === filter);
    else if (filter === "other")
      list = list.filter((p) => !CATEGORIES.includes(p.category));

    switch (sort) {
      case "recommended":
        // Keep the server's preference order (live lots in the buyer's category
        // order first, then upcoming). No client re-sort.
        break;
      case "recent":
        list.sort(
          (a, b) =>
            new Date(b.created_date || 0).getTime() -
            new Date(a.created_date || 0).getTime(),
        );
        break;
      case "ending":
        // No drop_time surfaced here: keep recent order.
        break;
      case "margin":
        list.sort(
          (a, b) =>
            (b.msrp - b.cost_price) / (b.cost_price || 1) -
            (a.msrp - a.cost_price) / (a.cost_price || 1),
        );
        break;
      case "lowcost":
        list.sort((a, b) => (a.cost_price || 0) - (b.cost_price || 0));
        break;
      case "units":
        list.sort(
          (a, b) => (b.units_available || 0) - (a.units_available || 0),
        );
        break;
    }
    return list;
  }, [combined, filter, sort]);

  return (
    <section className="py-20 px-4 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <p className="text-xs font-medium text-blue-400 tracking-widest uppercase mb-2">
          Inventory
        </p>
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-1.5">
          {authenticated ? "Picks for you" : "Live and upcoming lots"}
        </h2>
        <p className="text-gray-500">
          {authenticated
            ? "Handpicked lots matched to your buying profile."
            : "Curated inventory available through the Clearline network."}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((h) => (
          <button
            key={h.value}
            onClick={() => setFilter(h.value)}
            className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filter === h.value
                ? "bg-white text-black"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            {h.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-6">
        <span className="text-xs text-gray-500">Sort by</span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-white/5 text-white text-xs border border-white/10 rounded-full px-3 py-1.5 cursor-pointer focus:outline-none focus:border-white/30"
        >
          {sortOptions.map((h) => (
            <option key={h.value} value={h.value} className="bg-[#111] text-white">
              {h.label}
            </option>
          ))}
        </select>
      </div>
      {visible.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p className="mb-2">No lots match this filter yet.</p>
          <a href="/feed" className="text-blue-400 text-sm">
            Browse all inventory →
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.slice(0, 9).map((h) => (
            <LotCard key={h.id} lot={h} />
          ))}
        </div>
      )}
    </section>
  );
}
